import React, { useState } from "react";
import { TextField, Grid, CircularProgress } from "@mui/material";
import { useEffect } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import moment from "moment";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  TablePagination,
} from "@mui/material";
import TableSkeleton from "../../components/TableSkeleton";
import "./Report.css";
import axiosInstance from "../../utils/axiosInstance";

const Report = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [check, setCheck] = useState(false);
  const [goldPage, setGoldPage] = useState(0);
  const [silverPage, setSilverPage] = useState(0);
  const [goldRowsPerPage, setGoldRowsPerPage] = useState(10);
  const [silverRowsPerPage, setSilverRowsPerPage] = useState(10);
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");

  const goldData = data?.goldData?.length;
  const silverData = data?.silverData?.length;

  const handleGoldChangePage = (event, newPage) => {
    setGoldPage(newPage);
  };

  const handleGoldChangeRowsPerPage = (event) => {
    setGoldRowsPerPage(parseInt(event.target.value, 10));
    setGoldPage(0);
  };

  const goldPaginatedData = data?.goldData?.slice(
    goldPage * goldRowsPerPage,
    goldPage * goldRowsPerPage + goldRowsPerPage
  );

  const handleSilverChangePage = (event, newPage) => {
    setSilverPage(newPage);
  };

  const handleSilverChangeRowsPerPage = (event) => {
    setSilverRowsPerPage(parseInt(event.target.value, 10));
    setSilverPage(0);
  };

  const silverPaginatedData = data?.silverData?.slice(
    silverPage * silverRowsPerPage,
    silverPage * silverRowsPerPage + silverRowsPerPage
  );

  useEffect(() => {
    const today = new Date();
    const defaultEndDate = moment(today).format("DD MMM YYYY");
    const defaultStartDate = moment(today.setDate(today.getDate() - 30)).format(
      "DD MMM YYYY"
    );

    setSelectedStartDate(defaultStartDate);
    setSelectedEndDate(defaultEndDate);
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    fetchData(defaultStartDate, defaultEndDate);
  }, [check]);

  const fetchData = async () => {
    setLoading(true);
    const queryParams = new URLSearchParams({
      startDate: startDate,
      endDate: endDate,
    });

    try {
      const response = await axiosInstance.get(
        `/augmont/userReports/?${queryParams.toString()}`
      );
      if (response.data.error === false) {
        setLoading(false);
        setData(response?.data?.data);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error connecting to API", error);
    }
  };

  const sendPdf = async (blob, action) => {
    if (action === "download") setPdfLoading(true);
    if (action === "view") setViewLoading(true);
    if (action === "mail") setMailLoading(true);
    const selectedStartDate =
      startDate || moment().subtract(30, "days").format("DD MMM YYYY");
    const selectedEndDate = endDate || moment().format("DD MMM YYYY");

    try {
      const formData = new FormData();
      formData.append("file", blob, "Transaction_Report.pdf");
      formData.append("startDate", selectedStartDate);
      formData.append("endDate", selectedEndDate);

      const config = {
        headers: {
          "Content-Type": "multipart/form-data", // Set the Content-Type to multipart/form-data
        },
      };

      const response = await axiosInstance.post(
        "/augmont/userReports/sendMail",
        formData,
        config
      );

      if (response.data.error === false) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error connecting to API", error);
    } finally {
      setPdfLoading(false);
      setViewLoading(false);
      setMailLoading(false);
    }
  };

  const handleSearch = () => {
    const selectedEndDate = endDate || moment().format("DD-MM-YYYY");
    fetchData(startDate, selectedEndDate);

    setSelectedStartDate(startDate);
    setSelectedEndDate(selectedEndDate);
  };

  const handleRefresh = () => {
    setStartDate("");
    setEndDate("");
    setSelectedStartDate("");
    setSelectedEndDate("");
    setCheck(!check);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "gray",
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const generatePdf = () => {
    if (!data) return;

    const doc = new jsPDF();

    // const headerImg = `${process.env.PUBLIC_URL}/images/pdf_header.png`;
    // const footerImg = `${process.env.PUBLIC_URL}/images/pdf_footer.png`;

    // doc.addImage(headerImg, "PNG", 0, 0, 210, 25);

    const footerImageHeight = 15;
    const footer = (doc) => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(0, 0, 0);
        doc.text("Account Statement", pageWidth / 2, 15, { align: "center" });

        // doc.addImage(headerImg, "PNG", 0, 0, pageWidth - 0, 25);

        // doc.addImage(
        //   footerImg,
        //   "PNG",
        //   0,
        //   pageHeight - footerImageHeight - 0,
        //   pageWidth - 0,
        //   footerImageHeight
        // );

        // Add footer text
        // doc.setTextColor(238, 216, 129);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 5, {
          align: "justify",
        });
      }
    };

    // Add content and tables to the document
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 58, 72);

    const userDetails = [
      ["Name", data?.userDetails?.name || "N/A"],
      ["Phone", data?.userDetails?.phone || "N/A"],
      ["DOB", data?.userDetails?.dob || "N/A"],
      ["Email", data?.userDetails?.email || "N/A"],
      [
        "Address",
        data?.address
          ? `${data.address.line1 || "N/A"}, ${data.address.line2 || "N/A"}, ${
              data.address.city || "N/A"
            }, ${data.address.state || "N/A"}, ${data.address.zip || "N/A"}`
          : "N/A",
      ],
      ["Unique ID", data?.userDetails?.uniqueId || "N/A"],
    ];

    doc.autoTable({
      head: [["User Details", ""]],
      body: userDetails,
      startY: 30,
      theme: "plain",
      headStyles: {
        // textColor: [0, 58, 72],
        textColor: [0, 0, 0],
        fontSize: 16,
        fontStyle: "bold",
      },
      margin: {
        top: 30, // Top margin
        left: 10, // Left margin
        bottom: 20, // Bottom margin
        right: 10, // Right margin
      },
      didDrawPage: () => {
        // doc.addImage(headerImg, "PNG", 0, 0, 210, 25);
      },
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(
      `Statement From ${startDate} To ${endDate}`,
      10,
      doc.lastAutoTable.finalY + 10
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    // doc.setTextColor(0, 58, 72);
    doc.setTextColor(0, 0, 0);
    doc.text("Gold Transactions", 10, doc.lastAutoTable.finalY + 20);
    const goldData = data?.goldData || [];
    const goldTransactionRows = goldData.map((row) => [
      moment(row.createdAt).format("DD MMM YYYY"),
      row.description,
      row.type === "buy" ? row.gram : "",
      row.type === "sell" ? row.gram : "",
      row.amount,
      row.balance,
    ]);

    doc.setFont("helvetica", "bold");
    goldTransactionRows.push([
      "Total",
      "",
      data?.goldSummary?.totalBuyGram || "N/A",
      data?.goldSummary?.totalSellGram || "N/A",
      (data?.goldSummary?.totalBuyAmount || 0) -
        (data?.goldSummary?.totalSellAmount || 0),
      data?.goldData?.[data.goldData.length - 1]?.balance || "N/A",
    ]);

    doc.autoTable({
      head: [
        [
          "Txn Date",
          "Description",
          "Buy(g)",
          "Sell(g)",
          "Amount(Rs)",
          "Balance(g)",
        ],
      ],
      body: goldTransactionRows,
      startY: doc.lastAutoTable.finalY + 25,
      theme: "grid",
      headStyles: {
        // fillColor: [0, 58, 72],
        fillColor: "#DA2128",
        textColor: [255, 255, 255],
        fontSize: 12,
        fontStyle: "bold",
      },
      bodyStyles: {
        padding: 4,
      },
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      margin: {
        top: 30, // Top margin
        left: 10, // Left margin
        bottom: 20, // Bottom margin
        right: 10, // Right margin
      },
      didParseCell: (data) => {
        if (data.row.index === goldTransactionRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 10; // Apply bold style
        }
      },
      didDrawPage: () => {
        // doc.addImage(headerImg, "PNG", 0, 0, 210, 25);
      },
    });

    // Gold Summary Section
    const goldSummary = [
      ["Total Gold Bought (gm)", data?.goldSummary?.totalBuyGram || "N/A"],
      ["Total Gold Sold (gm)", data?.goldSummary?.totalSellGram || "N/A"],
      [
        "Total Gold Buy Amount (Rs)",
        data?.goldSummary?.totalBuyAmount || "N/A",
      ],
      [
        "Total Gold Sell Amount (Rs)",
        data?.goldSummary?.totalSellAmount || "N/A",
      ],
    ];

    doc.autoTable({
      head: [["Gold Summary", ""]],
      body: goldSummary,
      startY: doc.lastAutoTable.finalY + 5,
      theme: "plain",
      headStyles: {
        // textColor: [0, 58, 72],
        textColor: [0, 0, 0],
        fontSize: 16,
        fontStyle: "bold",
      },
      margin: {
        top: 30, // Top margin
        left: 10, // Left margin
        bottom: 20, // Bottom margin
        right: 10, // Right margin
      },
      didDrawPage: () => {
        // doc.addImage(headerImg, "PNG", 0, 0, 210, 25);
      },
    });

    // Silver Transactions Section
    const silverData = data?.silverData || [];
    const silverTransactionRows = silverData.map((row) => [
      moment(row.createdAt).format("DD MMM YYYY"),
      row.description,
      row.type === "buy" ? row.gram : "",
      row.type === "sell" ? row.gram : "",
      row.amount,
      row.balance,
    ]);

    silverTransactionRows.push([
      "Total",
      "",
      data?.silverSummary?.totalBuyGram || "N/A",
      data?.silverSummary?.totalSellGram || "N/A",
      (data?.silverSummary?.totalBuyAmount || 0) -
        (data?.silverSummary?.totalSellAmount || 0),
      data?.silverData?.[data.silverData.length - 1]?.balance || "N/A",
    ]);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    // doc.setTextColor(0, 58, 72);
    doc.setTextColor(0, 0, 0);
    doc.text("Silver Transactions", 10, doc.lastAutoTable.finalY + 10);

    doc.autoTable({
      head: [
        [
          "Txn Date",
          "Description",
          "Buy(g)",
          "Sell(g)",
          "Amount(Rs)",
          "Balance(g)",
        ],
      ],
      body: silverTransactionRows,
      startY: doc.lastAutoTable.finalY + 15,
      theme: "grid",
      headStyles: {
        // fillColor: [0, 58, 72],
        fillColor: "#DA2128",
        textColor: [255, 255, 255],
        fontSize: 12,
        fontStyle: "bold",
      },
      columnStyles: {
        2: { halign: "right" }, // "Buy(g)"
        3: { halign: "right" }, // "Sell(g)"
        4: { halign: "right" }, // "Amount"
        5: { halign: "right" }, // "Balance(g)"
      },
      margin: {
        top: 30, // Top margin
        left: 10, // Left margin
        bottom: 20, // Bottom margin
        right: 10, // Right margin
      },
      showHead: "everyPage",

      didParseCell: (data) => {
        if (data.row.index === silverTransactionRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 10; // Apply bold style
        }
      },

      didDrawPage: () => {
        // doc.addImage(headerImg, "PNG", 0, 0, 210, 25);
      },
    });

    // Silver Summary Section
    const silverSummary = [
      [
        "Total Silver Bought (gm)",
        `${data?.silverSummary?.totalBuyGram || "N/A"}`,
      ],
      [
        "Total Silver Sold (gm)",
        `${data?.silverSummary?.totalSellGram || "N/A"}`,
      ],
      [
        "Total Silver Buy Amount (Rs)",
        `${data?.silverSummary?.totalBuyAmount || "N/A"}`,
      ],
      [
        "Total Silver Sell Amount (Rs)",
        `${data?.silverSummary?.totalSellAmount || "N/A"}`,
      ],
    ];

    doc.autoTable({
      head: [["Silver Summary", ""]],
      body: silverSummary,
      startY: doc.lastAutoTable.finalY + 10,
      theme: "plain",
      headStyles: {
        // textColor: [0, 58, 72],
        textColor: [0, 0, 0],
        fontSize: 16,
        fontStyle: "bold",
      },
      margin: {
        top: 30, // Top margin
        left: 10, // Left margin
        bottom: 20, // Bottom margin
        right: 10, // Right margin
      },
      didDrawPage: () => {
        // doc.addImage(headerImg, "PNG", 0, 0, 210, 25);
        footer(doc);
      },
    });

    doc.setFont("helvetica", "italic");
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(
      "**This is a computer generated statement and does not require a signature.",
      10,
      doc.lastAutoTable.finalY + 10
    );

    footer(doc);

    const pdfOutput = doc.output("bloburl");
    return pdfOutput;
  };

  const handleDownload = async () => {
    setPdfLoading(true); // Start loading
    try {
      const pdfOutput = generatePdf();
      const link = document.createElement("a");
      link.href = pdfOutput;
      link.download = "Transaction_Report.pdf";
      link.click();
    } finally {
      setPdfLoading(false); // Stop loading
    }
  };

  const handleView = async () => {
    setViewLoading(true); // Start loading
    try {
      const pdfOutput = generatePdf();
      window.open(pdfOutput, "_blank");
    } finally {
      setViewLoading(false); // Stop loading
    }
  };

  const handleSendPdf = async (startDate, endDate) => {
    setMailLoading(true);
    try {
      const pdfOutput = generatePdf();
      const blob = await (await fetch(pdfOutput)).blob();
      await sendPdf(blob, startDate, endDate);
    } finally {
      setMailLoading(false);
    }
  };

  const {
    totalBuyGram: goldBuyGram,
    totalSellGram: goldSoldGram,
    totalBuyAmount: goldBuyAmount,
    totalSellAmount: goldSellAmount,
  } = data?.goldSummary || [];

  const {
    totalBuyGram: silverBuyGram,
    totalSellGram: silverSoldGram,
    totalBuyAmount: silverBuyAmount,
    totalSellAmount: silverSellAmount,
  } = data?.silverSummary || [];

  return (
    <div style={{ marginBottom: "3rem" }}>
      <h2>View Transaction</h2>
      <hr />
      <div className="mb-4">
        <div className="search-container mb-3" style={{ marginTop: "1rem" }}>
          <Grid
            container
            spacing={3}
            style={{ display: "flex", justifyContent: "end" }}
          >
            {/* Start Date and End Date Inputs */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={moment(startDate, "DD MMM YYYY").format("YYYY-MM-DD")}
                onChange={(e) =>
                  setStartDate(moment(e.target.value).format("DD MMM YYYY"))
                }
                inputProps={{ style: { height: "6px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={moment(endDate, "DD MMM YYYY").format("YYYY-MM-DD")}
                onChange={(e) =>
                  setEndDate(moment(e.target.value).format("DD MMM YYYY"))
                }
                inputProps={{ style: { height: "6px" } }}
              />
            </Grid>

            {/* Buttons in the Same Row */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    sx={{
                      backgroundColor: "#DA2128",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#DA2128",
                      },
                    }}
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    sx={{
                      backgroundColor: "#DA2128",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#DA2128",
                      },
                    }}
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    // startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
      <div style={{ marginBottom: "1rem" }}>
        {selectedStartDate && selectedEndDate ? (
          <h5>
            Statement From {selectedStartDate} To {selectedEndDate}
          </h5>
        ) : (
          <h5>
            Statement From {selectedStartDate} To {selectedEndDate}
          </h5>
        )}
      </div>
      <hr />
      {loading ? (
        <TableSkeleton />
      ) : data?.goldData?.length === 0 && data?.silverData?.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
            color: "gray",
          }}
        >
          No Transaction available
        </div>
      ) : (
        <>
          {/* Gold Data Table */}
          {data?.goldData?.length > 0 && (
            <>
              <h3>Gold Transactions</h3>
              <Paper sx={{ width: "100%", marginTop: "1rem" }}>
                <TableContainer component={Paper} sx={{ width: "100%" }}>
                  <Table
                    // sx={{ minWidth: 700 }}
                    sx={{ width: "100%" }}
                    aria-label="gold transactions table"
                  >
                    <TableHead>
                      <StyledTableRow>
                        <StyledTableCell>Txn Date</StyledTableCell>
                        <StyledTableCell>Description</StyledTableCell>
                        <StyledTableCell>Buy(g)</StyledTableCell>
                        <StyledTableCell>Sell(g)</StyledTableCell>
                        <StyledTableCell>Amount(₹)</StyledTableCell>
                        <StyledTableCell>Balance(g)</StyledTableCell>
                      </StyledTableRow>
                    </TableHead>
                    <TableBody>
                      {goldPaginatedData?.map((row, i) => (
                        <StyledTableRow key={i}>
                          <StyledTableCell sx={{ minWidth: "7rem" }}>
                            {moment(row.createdAt).format("DD MMM YYYY")}
                          </StyledTableCell>

                          <StyledTableCell sx={{ minWidth: "21rem" }}>
                            {row.description}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.type === "buy" ? row.gram : "-"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.type === "sell" ? row.gram : "-"}
                          </StyledTableCell>
                          <StyledTableCell>{row.amount}</StyledTableCell>
                          <StyledTableCell>{row.balance}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 30]}
                  component="div"
                  count={data?.goldData?.length || 0}
                  rowsPerPage={goldRowsPerPage}
                  page={goldPage}
                  onPageChange={handleGoldChangePage}
                  onRowsPerPageChange={handleGoldChangeRowsPerPage}
                  // style={{ zIndex: "3" }}
                  sx={{
                    zIndex: 3,
                    "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                      {
                        paddingTop: "0.8rem",
                      },
                  }}
                />
              </Paper>
            </>
          )}
          {goldData > 0 && (
            <div
              style={{
                backgroundColor: "#F3F2EE",
                margin: "2rem 0",
                borderRadius: "8px",
                padding: "1rem",
              }}
            >
              <h3>Gold Summary</h3>
              <div className="report_summary" style={{ marginTop: "1rem" }}>
                <div>
                  <div>Total Bought (Qty.)</div>
                  <div>Total Sold (Qty.)</div>
                  <div>Total Bought (Amt.)</div>
                  <div>Total Sold (Amt.)</div>
                </div>
                <div>
                  <div>-</div>
                  <div>-</div>
                  <div>-</div>
                  <div>-</div>
                </div>
                <div>
                  <div>{goldBuyGram}/gm</div>
                  <div>{goldSoldGram}/gm</div>
                  <div>₹{goldBuyAmount}</div>
                  <div>₹{goldSellAmount}</div>
                </div>
              </div>
            </div>
          )}
          <hr />

          {/* Silver Data Table */}
          {data?.silverData?.length > 0 && (
            <>
              <h3>Silver Transactions</h3>
              <Paper style={{ marginTop: "1rem" }}>
                <TableContainer component={Paper}>
                  <Table
                    sx={{ minWidth: 700 }}
                    aria-label="silver transactions table"
                  >
                    <TableHead>
                      <StyledTableRow>
                        <StyledTableCell>Txn Date</StyledTableCell>
                        <StyledTableCell>Description</StyledTableCell>
                        <StyledTableCell>Buy(g)</StyledTableCell>
                        <StyledTableCell>Sell(g)</StyledTableCell>
                        <StyledTableCell>Amount(₹)</StyledTableCell>
                        <StyledTableCell>Balance(g)</StyledTableCell>
                      </StyledTableRow>
                    </TableHead>
                    <TableBody>
                      {silverPaginatedData?.map((row, i) => (
                        <StyledTableRow key={i}>
                          <StyledTableCell sx={{ minWidth: "7rem" }}>
                            {moment(row.createdAt).format("DD MMM YYYY")}
                          </StyledTableCell>

                          <StyledTableCell sx={{ minWidth: "21rem" }}>
                            {row.description}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.type === "buy" ? row.gram : "-"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.type === "sell" ? row.gram : "-"}
                          </StyledTableCell>
                          <StyledTableCell>{row.amount}</StyledTableCell>
                          <StyledTableCell>{row.balance}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 30]}
                  component="div"
                  count={data?.silverData?.length || 0}
                  rowsPerPage={silverRowsPerPage}
                  page={silverPage}
                  onPageChange={handleSilverChangePage}
                  onRowsPerPageChange={handleSilverChangeRowsPerPage}
                  // style={{ zIndex: "3" }}
                  sx={{
                    zIndex: 3,
                    "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                      {
                        paddingTop: "0.8rem",
                      },
                  }}
                />
              </Paper>
            </>
          )}
          {silverData > 0 && (
            <div
              style={{
                backgroundColor: "#F3F2EE",
                margin: "2rem 0",
                borderRadius: "8px",
                padding: "1rem",
              }}
            >
              <h3>Silver Summary</h3>
              <div className="report_summary" style={{ marginTop: "1rem" }}>
                <div>
                  <div>Total Bought (Qty.)</div>
                  <div>Total Sold (Qty.)</div>
                  <div>Total Bought (Amt.)</div>
                  <div>Total Sold (Amt.)</div>
                </div>
                <div>
                  <div>-</div>
                  <div>-</div>
                  <div>-</div>
                  <div>-</div>
                </div>
                <div>
                  <div>{silverBuyGram}/gm</div>
                  <div>{silverSoldGram}/gm</div>
                  <div>₹{silverBuyAmount}</div>
                  <div>₹{silverSellAmount}</div>
                </div>
              </div>
            </div>
          )}
          <div className="mb-3 report_btn_container">
            <Button
              sx={{
                width: "100%",
                backgroundColor: "#DA2128",
                color: "white",
                "&:hover": {
                  backgroundColor: "#DA2128",
                },
              }}
              variant="contained"
              // style={{ marginRight: "1rem" }}
              color="primary"
              disabled={pdfLoading}
              onClick={() => {
                if (goldData > 0 || silverData > 0) {
                  handleDownload();
                } else {
                  toast.warn("No transactions available");
                }
              }}
              // sx={{
              //   ...(pdfLoading && {
              //     color: "white",
              //     "&.Mui-disabled": {
              //       color: "white",
              //     },
              //   }),
              // }}
            >
              {pdfLoading && (
                <CircularProgress sx={{ color: "white" }} size="22px" />
              )}
              &nbsp; Download PDF
            </Button>

            <Button
              sx={{
                width: "100%",
                backgroundColor: "#DA2128",
                color: "white",
                "&:hover": {
                  backgroundColor: "#DA2128",
                },
              }}
              variant="contained"
              // style={{ marginRight: "1rem" }}
              color="primary"
              disabled={viewLoading}
              onClick={() => {
                if (goldData > 0 || silverData > 0) {
                  handleView();
                } else {
                  toast.warn("No transactions available");
                }
              }}
              // sx={{
              //   ...(viewLoading && {
              //     color: "white",
              //     "&.Mui-disabled": {
              //       color: "white",
              //     },
              //   }),
              // }}
            >
              {viewLoading && (
                <CircularProgress sx={{ color: "white" }} size="22px" />
              )}
              &nbsp; View PDF
            </Button>

            <Button
              sx={{
                width: "100%",
                backgroundColor: "#DA2128",
                color: "white",
                "&:hover": {
                  backgroundColor: "#DA2128",
                },
              }}
              variant="contained"
              color="primary"
              disabled={mailLoading}
              onClick={() => {
                if (goldData > 0 || silverData > 0) {
                  handleSendPdf();
                } else {
                  toast.warn("No transactions available");
                }
              }}
              // sx={{
              //   ...(mailLoading && {
              //     color: "white",
              //     "&.Mui-disabled": {
              //       color: "white",
              //     },
              //   }),
              // }}
            >
              {mailLoading && (
                <CircularProgress sx={{ color: "white" }} size="22px" />
              )}
              &nbsp; Send PDF to mail
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Report;
